import type { RecipeInput } from '@fitness/api-client';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { RecipeEditor } from '@/components/food/RecipeEditor';
import { describeError } from '@/lib/api';
import { SAVED, showToast } from '@/lib/toast';
import { useRecipe, useReplaceRecipe } from '@/lib/queries/recipes';

export const Route = createFileRoute('/_app/food/recipes/$id/edit')({
  component: EditRecipePage,
});

/**
 * Edición de una receta.
 *
 * `PUT /v1/recipes/{id}` **reemplaza** la receta entera, así que el editor
 * parte siempre de la ficha completa cargada del servidor: guardar sin haberla
 * cargado borraría ingredientes y pasos.
 */
function EditRecipePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const query = useRecipe(id);
  const replace = useReplaceRecipe(id);

  function handleSubmit(input: RecipeInput): void {
    replace.mutate(input, {
      onSuccess: () => {
        showToast(SAVED.recipeSaved);
        void navigate({ to: '/food/recipes/$id', params: { id } });
      },
    });
  }

  return (
    <>
      <PageHeader
        title="Editar receta"
        subtitle={query.data?.name}
        action={
          <Link
            to="/food/recipes/$id"
            params={{ id }}
            aria-label="Volver a la receta"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando la receta…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar la receta"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <RecipeEditor
          recipe={query.data}
          onSubmit={handleSubmit}
          submitting={replace.isPending}
          errorTitle="No se han podido guardar los cambios"
          errorMessage={
            replace.isError ? describeError(replace.error, { saving: true }) : undefined
          }
          submitLabel="Guardar cambios"
          onCancel={() => void navigate({ to: '/food/recipes/$id', params: { id } })}
        />
      )}
    </>
  );
}
